/**
 * mad-video — Plyr player for YouTube, Vimeo, MP4 and HLS (hls.js or native Safari HLS).
 */

var attachSource = require('./media-source').attachSource;

// Bunny Stream rendition order: quality label → hls.js level index
var QUALITY_MAPPING = { '240': 0, '360': 1, '480': 2, '720': 3, '1080': 4 };

/**
 * Build the media element for Plyr: an embed placeholder for YouTube/Vimeo,
 * a <video> for MP4/HLS (the source is attached once Plyr is ready).
 */
function createMediaElement(options) {
  var media;

  if (options.type === 'youtube' || options.type === 'vimeo') {
    media = document.createElement('div');
    media.dataset.plyrProvider = options.type;
    media.dataset.plyrEmbedId = options.embedId;
  } else {
    media = document.createElement('video');
    media.setAttribute('playsinline', '');
    if (options.poster) media.poster = options.poster;
    if (options.lazy) media.preload = 'none';
  }

  media.id = 'mad-video-plyr-' + Math.random().toString(36).slice(2, 9);

  if (options.matomoTitle) {
    media.dataset.matomoTitle = options.matomoTitle;
    media.title = options.matomoTitle;
  }
  if (options.matomoResource) media.dataset.matomoResource = options.matomoResource;

  return media;
}

/**
 * Use the theme's local icon sprite (window.madVideoConfig, set by mad-video.php)
 * instead of Plyr's default on cdn.plyr.io.
 */
function withIconUrl(config) {
  var iconUrl = window.madVideoConfig && window.madVideoConfig.plyrIconUrl;
  if (iconUrl) config.iconUrl = iconUrl;
  return config;
}

/**
 * Plyr UI options from the element's data-player-config ({controls, settings, behavior},
 * built in trait-player-config.php). Without it (older cached markup) Plyr's defaults apply.
 */
function uiOptions(config) {
  if (!config) {
    return { settings: ['quality', 'speed'] };
  }

  var has = function (key) { return config.behavior.indexOf(key) !== -1; };

  return {
    controls: config.controls,
    settings: config.settings,
    clickToPlay: has('click_to_play'),
    hideControls: has('hide_controls'),
    keyboard: { focused: has('keyboard'), global: false },
    tooltips: { controls: has('tooltips'), seek: true },
    invertTime: has('invert_time'),
  };
}

/**
 * Unmute (unless autoplaying) and start playback.
 */
function startPlayback(player, options) {
  if (!options.autoplay) {
    player.volume = options.volume;
    player.muted = false;
  }
  var promise = player.play();
  if (promise && typeof promise.catch === 'function') {
    promise.catch(function (e) { console.warn('mad-video: play() rejected:', e); });
  }
}

/**
 * Attach the MP4/HLS source to the <video>. Returns false when the browser can't play it.
 */
function attachVideoSource(player, videoEl, options) {
  var source;
  try {
    source = attachSource(videoEl, options.url, {
      provider: options.type,
      lazy: options.lazy,
      onError: options.onError,
    });
  } catch (err) {
    console.error('mad-video: ' + err.message);
    return false;
  }

  videoEl._hlsInstance = source.hls; // read by the quality switch and _teardown()
  if (options.lazy) {
    player.once('play', source.startLoad);
  }
  return true;
}

/**
 * Create a Plyr player inside `mount`.
 *
 * @param {HTMLElement} mount   Emptied, then receives the media element.
 * @param {Object}   options
 * @param {string}   options.type         'youtube' | 'vimeo' | 'hls' | 'mp4'
 * @param {string}   options.url
 * @param {string}   [options.embedId]    YouTube/Vimeo ID
 * @param {boolean}  options.autoplay     Muted autoplay
 * @param {boolean}  options.startOnReady Start playback as soon as Plyr is ready (after a click or for autoplay)
 * @param {boolean}  [options.lazy]       MP4/HLS: download nothing until the viewer presses play
 * @param {string}   [options.poster]     MP4/HLS poster image
 * @param {boolean}  options.loop
 * @param {number}   options.volume       0–1, used when not autoplaying
 * @param {Object}   [options.config]     {controls, settings, behavior} from data-player-config
 * @param {string}   [options.matomoTitle]
 * @param {string}   [options.matomoResource]
 * @param {Function} options.onReady
 * @param {Function} options.onPlaying
 * @param {Function} options.onError      Playback failed — reset the element
 * @returns {Plyr|null}
 */
function createPlyrPlayer(mount, options) {
  var isEmbed = options.type === 'youtube' || options.type === 'vimeo';
  var videoEl = createMediaElement(options);

  mount.innerHTML = '';
  mount.appendChild(videoEl);

  if (window._paq && typeof window._paq.push === 'function') {
    window._paq.push(['MediaAnalytics::scanForMedia']);
  }

  try {
    var player = new Plyr(videoEl, withIconUrl(Object.assign(uiOptions(options.config), {
      muted: options.autoplay,
      autoplay: options.autoplay,
      loop: { active: options.loop },
      storage: { enabled: false },
      playsinline: true,
      youtube: { noCookie: true, rel: 0, modestbranding: 1 },
      quality: {
        default: '720',
        options: Object.keys(QUALITY_MAPPING).reverse(),
        forced: true,
        onChange: function (newQuality) {
          var hlsInstance = videoEl._hlsInstance;
          if (hlsInstance) {
            hlsInstance.currentLevel = QUALITY_MAPPING[newQuality] != null
              ? QUALITY_MAPPING[newQuality]
              : -1;
          }
        },
      },
    })));

    player.on('ready', function () {
      if (!isEmbed && !attachVideoSource(player, videoEl, options)) {
        options.onError();
        return;
      }
      if (options.startOnReady) {
        startPlayback(player, options);
      } else if (!options.autoplay) {
        // Started later by the viewer: play with the configured volume
        player.volume = options.volume;
      }
      options.onReady();
    });

    player.on('playing', function () {
      options.onPlaying();
    });

    player.on('error', function (e) {
      console.error('mad-video: Plyr error', e);
      options.onError();
    });

    return player;

  } catch (err) {
    console.error('mad-video: Error initialising Plyr:', err);
    options.onError();
    return null;
  }
}

module.exports = { createPlyrPlayer: createPlyrPlayer };
